// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NJBadge,
  NJButton,
  NJCard,
  NJHeading,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJListDeprecated,
  NJListItemDeprecated,
  NJSidebarCloseDeprecated,
  NJSidebarDeprecated,
  NJSidebarDividerDeprecated,
  NJSidebarMenuDeprecated,
  NJSpinner,
  NJTag,
  NJText,
  NJTooltip,
} from '@engie-group/fluid-design-system-react';

const PRIMARY_NAV = [
  { id: 'networks', icon: 'account_tree', label: 'Networks' },
  { id: 'calculations', icon: 'legend_toggle', label: 'Calculations' },
  { id: 'coreai', icon: 'auto_awesome', label: 'Nemo CoreAI' },
];
const PRIMARY_FOOTER = [
  { id: 'user-guide', icon: 'help_outline', label: 'User guide' },
  { id: 'profile', icon: 'account_circle', label: 'Profile' },
  { id: 'disconnect', icon: 'power_settings_new', label: 'Disconnect' },
];
const SECONDARY_NAV = [
  { id: 'general', icon: 'remove_red_eye', label: 'General' },
  { id: 'import-export', icon: 'swap_horiz', label: 'Import/export' },
  { id: 'network-editor', icon: 'map', label: 'Network editor' },
  { id: 'production', icon: 'factory', label: 'Production' },
  { id: 'plannings', icon: 'insert_invitation', label: 'Plannings' },
  { id: 'optimizations', icon: 'stacked_bar_chart', label: 'Optimizations' },
];

const SIDEBAR_ITEM_CLASS =
  'border-none hover:bg-transparent sidebar-menu-item';
const PRIMARY_SIDEBAR_WIDTH_OPEN = 300;
const PRIMARY_SIDEBAR_WIDTH_FOLDED = 70;

import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import DataTable from '@shared/DataTable';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { useOptimizationCloneList } from './hooks';
import NewOptiFormTimelessEauFroide from '@prototypes/size2mix/new-opti-form-timeless-eau-froide/index';
import TrajectoryOptimizationCreateForm from '@prototypes/size2mix/trajectory-optimization-create-form/index';
import NewOptiFormShortTerm from '@prototypes/size2mix/new-opti-form-short-term/index';
import type {
  OptimizationAttribute,
  OptimizationRow,
  OptimizationStatus,
  OptimizationType,
} from './types';

export const meta = {
  title: "Optimisations - Clone d'une optimisation",
  description:
    "Liste des optimisations avec action clone ouvrant le formulaire du meme type pre-rempli, modifiable et lancable.",
  tags: ['optimisation', 'list', 'clone', 'form'],
  author: 'dv5886',
  status: 'ready' as const,
  category: 'optimisations/list',
};

const STATUS_LABELS: Record<OptimizationStatus, string> = {
  running: 'En cours',
  success: 'Succès',
  preparing: 'En préparation',
  failed: 'Échec',
};

const TYPE_LABELS: Record<OptimizationType, string> = {
  timeless: 'Timeless',
  trajectory: 'Trajectory',
  'short-term': 'Short term',
};

function buildCloneName(sourceName: string): string {
  return `Clone - ${sourceName}`;
}

function toDateTimeLocalValue(raw: string): string {
  return raw.slice(0, 16);
}

function WarningIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5421)">
        <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" fill="#FF6103" fillOpacity="0.73" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5421">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5424)">
        <path d="M15.73 3H8.27L3 8.27V15.73L8.27 21H15.73L21 15.73V8.27L15.73 3ZM12 17.3C11.28 17.3 10.7 16.72 10.7 16C10.7 15.28 11.28 14.7 12 14.7C12.72 14.7 13.3 15.28 13.3 16C13.3 16.72 12.72 17.3 12 17.3ZM13 13H11V7H13V13Z" fill="#DB3735" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5424">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function MedicalServicesIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5505)">
        <path d="M20 6H16V4C16 2.9 15.1 2 14 2H10C8.9 2 8 2.9 8 4V6H4C2.9 6 2 6.9 2 8V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V8C22 6.9 21.1 6 20 6ZM10 4H14V6H10V4ZM16 15H13V18H11V15H8V13H11V10H13V13H16V15Z" fill="#DB3735" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5505">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function StatusIconTooltip({ label, children }: { label: string; children: JSX.Element }) {
  return (
    <NJTooltip text={label} hasArrow placement="top">
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{children}</span>
    </NJTooltip>
  );
}

function getStatusIcons(row: OptimizationRow): Array<JSX.Element> {
  if (row.id === 'opt-287156145-b') {
    return [
      <StatusIconTooltip key="warning" label="Slack">
        <WarningIcon />
      </StatusIconTooltip>,
      <StatusIconTooltip key="report" label="Infeasible">
        <ReportIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716700') {
    return [
      <StatusIconTooltip key="warning" label="Slack">
        <WarningIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716874-a') {
    return [
      <StatusIconTooltip key="medical-services" label="Erreur">
        <MedicalServicesIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716874-b') {
    return [
      <StatusIconTooltip key="report" label="Infeasible">
        <ReportIcon />
      </StatusIconTooltip>,
    ];
  }

  return [];
}

function StatusCell({ value, row }: { value: OptimizationStatus; row: OptimizationRow }) {
  // En cours: information, Succès: success, En préparation: neutral, Échec: danger
  const badgeProps = {
    running: { variant: 'information' as const },
    success: { variant: 'success' as const },
    preparing: { variant: 'neutral' as const },
    failed: { variant: 'danger' as const },
  }[value];
  const statusIcons = getStatusIcons(row);
  return (
    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', height: '100%', gap: 8 }}>
      <NJBadge
        variant={badgeProps.variant}
        emphasis="subtle"
        scale="md"
        style={{
          borderRadius: '9999px',
          padding: '0 8px',
          height: 24,
          width: 'auto',
          minWidth: 'fit-content',
          whiteSpace: 'nowrap',
        }}
      >
        {STATUS_LABELS[value]}
      </NJBadge>
      {statusIcons.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {statusIcons}
        </div>
      )}
    </div>
  );
}

function TypeCell({ value }: { value: OptimizationType }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%' }}>
      <NJTag
        label={TYPE_LABELS[value]}
        variant="ultramarine"
        size="xs"
        style={{
          height: 20,
          width: 'fit-content',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
        }}
      />
    </div>
  );
}

const ATTRIBUTE_MAP: Record<string, { color: string; label: string; description: string }> = {
  P: {
    color: 'var(--nj-core-color-pink-300)',
    label: 'P',
    description: 'Réserve de puissance',
  },
  R: {
    color: 'var(--nj-core-color-teal-400)',
    label: 'R',
    description: 'Redimensionnement',
  },
  F: {
    color: 'var(--nj-core-color-purple-400)',
    label: 'F',
    description: 'Foisonnement par canalisation',
  },
  A: {
    color: '#0288d1',
    label: 'A',
    description: 'Foisonnement Automatique',
  },
  C: {
    color: '#d32f2f',
    label: 'C',
    description: 'Chemin Critique',
  },
};

const ATTRIBUTE_PRIORITY: Record<string, number> = {
  R: 0,
  P: 1,
  A: 2,
  C: 3,
  F: 4,
};

function AttributeIcon({ letter, showPlus }: { letter: string; showPlus?: boolean }) {
  const attr = ATTRIBUTE_MAP[letter];
  if (!attr) return null;

  const chip = (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: attr.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 13,
        position: 'relative',
        cursor: 'default',
      }}
    >
      {letter}
      {showPlus && (
        <div
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            background: 'var(--nj-core-color-blue-400)',
            width: 14,
            height: 14,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            border: '2px solid white',
          }}
        >
          +
        </div>
      )}
    </div>
  );

  return (
    <NJTooltip text={`${letter} - ${attr.description}`} hasArrow placement="top">
      {chip}
    </NJTooltip>
  );
}

function AttributesCell({ attributes }: { attributes: OptimizationAttribute[] }) {
  if (!attributes || attributes.length === 0) {
    return <span style={{ color: 'var(--nj-semantic-color-text-neutral-weak-default)' }}>—</span>;
  }

  const orderedAttributes = [...attributes].sort((left, right) => {
    const leftPriority = ATTRIBUTE_PRIORITY[left.label] ?? Number.MAX_SAFE_INTEGER;
    const rightPriority = ATTRIBUTE_PRIORITY[right.label] ?? Number.MAX_SAFE_INTEGER;
    return leftPriority - rightPriority;
  });

  const displayed = orderedAttributes.slice(0, 2);
  const remaining = orderedAttributes.slice(2);
  const hasMore = remaining.length > 0;
  const tooltipLines = remaining
    .map((attr) => {
      const details = ATTRIBUTE_MAP[attr.label];
      return details ? `${attr.label} - ${details.description}` : attr.label;
    })
    .join('\n');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', height: '100%' }}>
      {displayed.map((attr) => (
        <AttributeIcon
          key={attr.id}
          letter={attr.label}
          showPlus={false}
        />
      ))}
      {hasMore && (
        <NJTooltip
          text={
            <span
              style={{
                display: 'inline-block',
                textAlign: 'left',
                whiteSpace: 'pre-line',
              }}
            >
              {tooltipLines}
            </span>
          }
          hasArrow
          placement="top"
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--nj-core-color-blue-400)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'default',
            }}
          >
            +
          </div>
        </NJTooltip>
      )}
    </div>
  );
}

function NameCell({ value, description }: { value: string; description?: string }) {
  const nameContent = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <NJText
        as="span"
        scale="sm"
        style={{
          color: 'var(--nj-semantic-color-text-brand-primary-default)',
          fontWeight: 600,
          textDecoration: 'underline',
          textDecorationColor: 'transparent',
        }}
      >
        {value}
      </NJText>
    </span>
  );

  if (!description) {
    return nameContent;
  }

  return (
    <NJTooltip text={description} hasArrow placement="top">
      {nameContent}
    </NJTooltip>
  );
}

interface ActionsCellProps {
  row: OptimizationRow;
  onClone: (row: OptimizationRow) => void;
  onPlay: (row: OptimizationRow) => void;
  onStop: (row: OptimizationRow) => void;
}

function ActionsCell({ row, onClone, onPlay, onStop }: ActionsCellProps) {
  const isRunning = row.status === 'running';
  const isPreparing = row.status === 'preparing';
  const isFinalStatus = row.status === 'failed' || row.status === 'success';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-core-space-50)', height: '100%' }}>
      {/* Primary action: Clone (failed/success), Play (preparing), or Stop (running) */}
      {isPreparing && (
        <NJTooltip text="Lancer" hasArrow placement="top">
          <NJIconButton
            icon="play_circle_outline"
            label={`Lancer ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onPlay(row)}
          />
        </NJTooltip>
      )}
      {isRunning && (
        <NJTooltip text="Arrêter" hasArrow placement="top">
          <NJIconButton
            icon="stop_circle"
            label={`Arrêter ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onStop(row)}
          />
        </NJTooltip>
      )}
      {isFinalStatus && (
        <NJTooltip text="Cloner" hasArrow placement="top">
          <NJIconButton
            icon="content_copy"
            label={`Cloner ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onClone(row)}
          />
        </NJTooltip>
      )}

      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '18px',
          background: 'var(--nj-semantic-color-border-neutral-minimal-default)',
        }}
      />

      <NJTooltip text="Supprimer" hasArrow placement="top">
        <NJIconButton
          icon="delete_outline"
          label={`Supprimer ${row.name}`}
          variant="tertiary"
          scale="xs"
        />
      </NJTooltip>
    </div>
  );
}

export default function CloneOptimisationPrototype() {
  const { data, isLoading, error } = useOptimizationCloneList();

  const [isPrimaryMenuFolded, setIsPrimaryMenuFolded] = useState(true);
  const [rows, setRows] = useState<OptimizationRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [selectedOptimization, setSelectedOptimization] = useState<OptimizationRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newFormType, setNewFormType] = useState<OptimizationType | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data) {
      setRows(data.rows);
      setTotal(data.total);
    }
  }, [data]);

  const openCloneModal = (row: OptimizationRow) => {
    setFeedbackMessage('');
    setNewFormType(null);
    setSelectedOptimization(row);
    setFormOpen(true);
  };

  const openNewForm = (type: OptimizationType) => {
    setDropdownOpen(false);
    setFeedbackMessage('');
    setSelectedOptimization(null);
    setNewFormType(type);
    setFormOpen(true);
  };

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handlePlay = (row: OptimizationRow) => {
    setFeedbackMessage(`Lancement de l'optimisation "${row.name}"...`);
    // TODO: API call to launch optimization
    setTimeout(() => {
      setRows((previous) =>
        previous.map((r) =>
          r.id === row.id ? { ...r, status: 'running' as OptimizationStatus } : r,
        ),
      );
      setFeedbackMessage(`Optimisation "${row.name}" lancée.`);
    }, 500);
  };

  const handleStop = (row: OptimizationRow) => {
    setFeedbackMessage(`Arrêt de l'optimisation "${row.name}"...`);
    // TODO: API call to stop optimization
    setTimeout(() => {
      setRows((previous) =>
        previous.map((r) =>
          r.id === row.id ? { ...r, status: 'failed' as OptimizationStatus } : r,
        ),
      );
      setFeedbackMessage(`Optimisation "${row.name}" arrêtée.`);
    }, 500);
  };

  const columns: ColDef<OptimizationRow>[] = useMemo(
    () => [
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 160,
        maxWidth: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        filterValueGetter: ({ data }) => (data ? STATUS_LABELS[data.status] : ''),
        cellRenderer: (params: ICellRendererParams<OptimizationRow, OptimizationStatus>) => {
          if (!params.data) {
            return null;
          }
          return <StatusCell value={params.value as OptimizationStatus} row={params.data} />;
        },
        valueFormatter: (params) => STATUS_LABELS[params.value as OptimizationStatus],
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 130,
        maxWidth: 160,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        cellRenderer: TypeCell,
        valueFormatter: (params) => TYPE_LABELS[params.value as OptimizationType],
      },
      {
        headerName: 'Name',
        field: 'name',
        minWidth: 280,
        flex: 2,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        cellRenderer: (params: ICellRendererParams<OptimizationRow, string>) => {
          if (!params.data) {
            return null;
          }

          return <NameCell value={params.value as string} description={params.data.description} />;
        },
      },
      {
        headerName: 'Steps',
        field: 'steps',
        minWidth: 130,
        maxWidth: 160,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
      },
      {
        headerName: 'Attributes',
        field: 'attributes',
        minWidth: 150,
        maxWidth: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterValueGetter: ({ data }) => data?.attributes.map((attribute) => attribute.label).join(' ') || '',
        cellRenderer: ({ value }: { value: OptimizationRow['attributes'] }) => (
          <AttributesCell attributes={value} />
        ),
      },
      {
        headerName: 'Created by',
        field: 'createdBy',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
      },
      {
        headerName: 'Date',
        field: 'date',
        minWidth: 160,
        sort: 'desc',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
      },
      {
        headerName: 'Actions',
        field: 'id',
        minWidth: 100,
        maxWidth: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params: ICellRendererParams<OptimizationRow>) => {
          if (!params.data) {
            return null;
          }
          return (
            <ActionsCell
              row={params.data}
              onClone={openCloneModal}
              onPlay={handlePlay}
              onStop={handleStop}
            />
          );
        },
      },
    ],
    [],
  );

  return (
    <PrototypeStudioLayout title={meta.title}>
      <style>
        {`
          .clone-opti-primary-sidebar {
            --nj-sidebar-deprecated-top: 56px;
            --nj-sidebar-deprecated-height: calc(100% - 56px);
          }
          .sidebar-nav-item {
            height: 48px !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background-color: transparent !important;
            box-shadow: none !important;
            display: flex !important;
            align-items: center !important;
          }
          .sidebar-nav-item.active {
            background-color: transparent !important;
            border-left: 3px solid var(--nj-semantic-color-border-brand-primary-default) !important;
          }
          .sidebar-nav-item.active .nj-list-deprecated__item,
          .sidebar-nav-item.active .nj-list-deprecated__item:hover,
          .sidebar-nav-item.active .nj-list-deprecated__item:focus {
            background-color: var(--nj-core-color-blue-alpha-100) !important;
          }
          .sidebar-nav-item.active:hover,
          .sidebar-nav-item.active:hover li,
          .sidebar-nav-item.active li:hover {
            background-color: transparent !important;
          }
          .sidebar-nav-item.active .material-icons {
            color: var(--nj-semantic-color-text-brand-primary-default) !important;
          }
          .sidebar-nav-item.active .nj-list-deprecated__item-content {
            color: var(--nj-semantic-color-text-neutral-primary-default) !important;
            font-weight: 700 !important;
            white-space: nowrap !important;
          }
          .sidebar-nav-item.active .nj-list-deprecated__item {
            padding-left: 32px !important;
          }
          .sidebar-nav-item button,
          .sidebar-nav-item [role="button"],
          .sidebar-nav-item li {
            height: 48px !important;
            width: 100% !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 8px !important;
            margin: 0 !important;
            gap: 8px !important;
            border: none !important;
            box-shadow: none !important;
            color: var(--nj-semantic-color-text-neutral-primary-default) !important;
            font-size: 14px !important;
            font-weight: 500 !important;
            white-space: nowrap !important;
          }
          .sidebar-nav-item.active button,
          .sidebar-nav-item.active [role="button"],
          .sidebar-nav-item.active li {
            color: var(--nj-semantic-color-text-brand-primary-default) !important;
            font-weight: 600 !important;
          }
          .sidebar-nav-item button,
          .sidebar-nav-item [role="button"],
          .sidebar-nav-item li,
          .sidebar-nav-item * {
            box-sizing: border-box !important;
          }
          .sidebar-nav-item.active button,
          .sidebar-nav-item.active [role="button"],
          .sidebar-nav-item.active li {
            color: var(--nj-semantic-color-text-brand-primary-default) !important;
            font-weight: 600 !important;
          }
        `}
      </style>
      <div style={{ display: 'flex', height: '100vh', background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }}>
        {/* Primary sidebar (frontend component pattern, opened) */}
        <div
          style={{
            width: isPrimaryMenuFolded
              ? PRIMARY_SIDEBAR_WIDTH_FOLDED
              : PRIMARY_SIDEBAR_WIDTH_OPEN,
            borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
            transition: 'width 180ms ease',
            overflow: 'hidden',
          }}
        >
          <NJSidebarDeprecated
            isFolded={isPrimaryMenuFolded}
            className="min-h-screen clone-opti-primary-sidebar"
            style={{
              top: 56,
              height: 'calc(100% - 56px)',
              width: isPrimaryMenuFolded
                ? PRIMARY_SIDEBAR_WIDTH_FOLDED
                : PRIMARY_SIDEBAR_WIDTH_OPEN,
            }}
          >
            <div
              style={{
                height: 120,
                borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="0.25" y="0.25" width="29.5" height="29.5" rx="5.75" fill="white" stroke="#7FADEF" strokeWidth="0.5" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.472 16.7014C21.472 18.0698 21.0454 19.3387 20.3178 20.3819H9.74745C9.01989 19.3387 8.59324 18.0698 8.59324 16.7014C8.59324 13.1441 11.4762 10.2604 15.0326 10.2604C16.3763 10.2604 17.6237 10.672 18.656 11.3761C19.5259 11.0743 20.5385 10.7404 21.7096 10.372C20.0332 8.60315 17.6617 7.5 15.0326 7.5C9.95213 7.5 5.8335 11.6196 5.8335 16.7014C5.8335 19.2092 6.83648 21.4826 8.46316 23.1424H21.6021C23.2288 21.4826 24.2318 19.2092 24.2318 16.7014C24.2318 15.5656 24.026 14.4778 23.6497 13.4734C22.7947 14.2595 22.0358 14.9399 21.3648 15.5248C21.4352 15.9063 21.472 16.2996 21.472 16.7014ZM13.3927 17.2456C13.9008 18.1258 15.0158 18.4332 15.8833 17.9323C16.7507 17.4313 18.7945 15.7198 22.8164 11.8035C17.4146 13.3292 14.9108 14.2439 14.0434 14.7448C13.176 15.2457 12.8846 16.3654 13.3927 17.2456Z"
                  fill="url(#paint0_linear_2552_2549)"
                />
                <defs>
                  <linearGradient id="paint0_linear_2552_2549" x1="-2.84877" y1="15.1606" x2="11.9977" y2="32.6226" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#00AAFF" />
                    <stop offset="0.96875" stopColor="#23D2B5" />
                  </linearGradient>
                </defs>
              </svg>
              {!isPrimaryMenuFolded && (
                <NJHeading as="h2" scale="md" style={{ margin: 0, color: 'var(--nj-semantic-color-text-brand-primary-default)' }}>
                  NeMo
                </NJHeading>
              )}
            </div>

            <NJSidebarMenuDeprecated>
              {PRIMARY_NAV.map((item) => (
                <NJListItemDeprecated
                  key={item.id}
                  icon={{ name: item.icon, size: 'md' }}
                  type="link"
                  isActive={item.id === 'networks'}
                  className={SIDEBAR_ITEM_CLASS}
                >
                  {item.label}
                </NJListItemDeprecated>
              ))}
            </NJSidebarMenuDeprecated>

            <NJSidebarMenuDeprecated isFooterNav>
              <NJListItemDeprecated
                icon={{ name: 'history', size: 'md' }}
                type="list"
                className={SIDEBAR_ITEM_CLASS}
              >
                Old version
              </NJListItemDeprecated>
              <NJSidebarDividerDeprecated />
              {PRIMARY_FOOTER.map((item) => (
                <NJListItemDeprecated
                  key={item.id}
                  icon={{ name: item.icon, size: 'md' }}
                  type="list"
                  className={SIDEBAR_ITEM_CLASS}
                >
                  {item.label}
                </NJListItemDeprecated>
              ))}
            </NJSidebarMenuDeprecated>

            <NJSidebarCloseDeprecated
              data-testid="toggle_main_navbar"
              onClick={() => setIsPrimaryMenuFolded((previous) => !previous)}
            >
              <span className="text-[var(--nj-core-color-grey-600)]">
                {isPrimaryMenuFolded ? 'Expand' : 'Reduce'}
              </span>
            </NJSidebarCloseDeprecated>
          </NJSidebarDeprecated>
        </div>

        {/* Secondary sidebar */}
        <nav style={{ width: 230, background: 'var(--nj-semantic-color-background-neutral-primary-default)', borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '24px 0 12px 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
              <g clipPath="url(#clip0_2552_5329)">
                <path d="M17.7143 2H6.28571C5.02857 2 4.01143 3 4.01143 4.22222L4 22L12 18.6667L20 22V4.22222C20 3 18.9714 2 17.7143 2Z" fill="#007ACD" />
                <path d="M14.9844 11.6357C14.9843 11.6337 14.9841 11.6318 14.9839 11.6299C14.9083 10.8171 14.4723 8.52986 11.9999 6C9.52643 8.53123 9.09116 10.8194 9.01576 11.6311L9.01551 11.6341C8.99751 11.8292 9.00007 11.9385 9.00019 11.9436V11.9448C9.00019 13.6321 10.3432 15 12 15C13.6568 15 15 13.6321 15 11.9448L14.9998 11.9424C15 11.9323 15.0019 11.8249 14.9844 11.6357Z" fill="white" />
              </g>
              <defs>
                <clipPath id="clip0_2552_5329">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <NJHeading
              scale="sm"
              as="h2"
              style={{
                margin: 0,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              test PUC
            </NJHeading>
          </div>
          <div style={{ flex: 1, paddingTop: 12, paddingLeft: 0, paddingRight: 0, display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: 16 }}>
            <NJListDeprecated style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {SECONDARY_NAV.filter((item) => item.id !== 'optimizations').map((item) => (
                <div key={item.id} className="sidebar-nav-item">
                  <NJListItemDeprecated
                    icon={{ name: item.icon, size: 'md' }}
                    type="link"
                    className={SIDEBAR_ITEM_CLASS}
                  >
                    {item.label}
                  </NJListItemDeprecated>
                </div>
              ))}

              <div
                className="sidebar-nav-item active"
                style={{
                  position: 'relative',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    backgroundColor: '#0052CC',
                  }}
                />
                <NJListItemDeprecated
                  icon={{ name: 'stacked_bar_chart', size: 'md' }}
                  type="dropdown"
                  className="hover:bg-transparent sidebar-menu-item"
                >
                  Optimizations
                </NJListItemDeprecated>
              </div>
            </NJListDeprecated>
          </div>
        </nav>

        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* App header bar */}
          <header
            style={{
              height: 48,
              borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 24px',
              gap: 12,
              background: 'var(--nj-semantic-color-background-neutral-primary-default)',
            }}
          >
            <NJButton label="Study" variant="secondary" scale="xs" />
            <NJIconButton icon="search" label="Search" variant="tertiary" scale="xs" />
            <NJIconButton icon="schedule" label="History" variant="tertiary" scale="xs" />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                cursor: 'pointer',
              }}
            >
              EN
              <NJIcon name="keyboard_arrow_down" style={{ fontSize: 16 }} />
            </div>
          </header>

          {/* Main content */}
          <main
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <NJHeading scale="md" style={{ fontSize: 16, lineHeight: '24px', margin: 0 }}>
                Optimisations list ({total})
              </NJHeading>
              {/* Create new optimisation dropdown */}
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 0,
                    borderRadius: 'var(--nj-core-border-radius-sm)',
                    overflow: 'hidden',
                    background: 'var(--nj-semantic-color-background-brand-primary-default)',
                  }}
                >
                  <NJButton
                    label="Créer une nouvelle optimisation"
                    variant="primary"
                    scale="sm"
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    onClick={() => setDropdownOpen((o) => !o)}
                  />
                  <div style={{ width: '1px', background: 'rgba(255,255,255,0.4)', alignSelf: 'stretch' }} />
                  <NJIconButton
                    icon="keyboard_arrow_down"
                    label="Options"
                    variant="primary"
                    scale="sm"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    onClick={() => setDropdownOpen((o) => !o)}
                  />
                </div>
                {dropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 4,
                      background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                      border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                      borderRadius: 'var(--nj-core-border-radius-md)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      minWidth: 240,
                      zIndex: 100,
                      overflow: 'hidden',
                    }}
                  >
                    {[
                      { type: 'timeless' as OptimizationType, label: 'Paramétrique Atemporelle', icon: 'access_time' },
                      { type: 'short-term' as OptimizationType, label: 'Court terme', icon: 'timelapse' },
                      { type: 'trajectory' as OptimizationType, label: 'Trajectoire', icon: 'show_chart' },
                    ].map((option) => (
                      <button
                        key={option.type}
                        onClick={() => openNewForm(option.type)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: 14,
                          color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--nj-semantic-color-background-neutral-secondary-default)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <NJIcon name={option.icon} style={{ fontSize: 20, color: 'var(--nj-semantic-color-text-brand-primary-default)', flexShrink: 0 }} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {feedbackMessage && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: '#e8f5e9',
                  borderRadius: 'var(--nj-core-border-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  border: '1px solid #a5d6a7',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flex: 1,
                  }}
                >
                  <NJIcon name="check_circle" style={{ color: '#2e7d32', flexShrink: 0 }} />
                  <span
                    style={{
                      color: '#2e7d32',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {feedbackMessage}
                  </span>
                </div>
                <NJIconButton
                  icon="close"
                  label="Close message"
                  variant="tertiary"
                  scale="xs"
                  onClick={() => setFeedbackMessage('')}
                  style={{ flexShrink: 0, marginRight: '-4px' }}
                />
              </div>
            )}

            <NJCard
              style={{
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                borderRadius: 'var(--nj-core-border-radius-md)',
                padding: 0,
              }}
            >
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                  <NJSpinner />
                </div>
              )}
              {error && (
                <NJInlineMessage variant="error">Unable to load the optimisations list.</NJInlineMessage>
              )}
              {!isLoading && !error && (
                <DataTable<OptimizationRow> testId="clone-optimisation-table" header={columns} data={rows} />
              )}
            </NJCard>
          </main>
        </div>
      </div>

      {formOpen && (
        <>
          {/* Full-screen backdrop — dims the whole app, click to dismiss */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
              background: 'rgba(0, 0, 0, 0.45)',
            }}
            onClick={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
          />
          {/* Right-side panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: `min(1300px, calc(100vw - ${isPrimaryMenuFolded ? PRIMARY_SIDEBAR_WIDTH_FOLDED : PRIMARY_SIDEBAR_WIDTH_OPEN}px))`,
              zIndex: 9999,
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
              overflow: 'auto',
              boxShadow: '-4px 0 32px rgba(0, 0, 0, 0.18)',
            }}
          >
            {(selectedOptimization?.type ?? newFormType) === 'timeless' ? (
              <NewOptiFormTimelessEauFroide
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : (selectedOptimization?.type ?? newFormType) === 'trajectory' ? (
              <TrajectoryOptimizationCreateForm
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  startDate: toDateTimeLocalValue(selectedOptimization.seedForm.startDate),
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : (selectedOptimization?.type ?? newFormType) === 'short-term' ? (
              <NewOptiFormShortTerm
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  startDate: toDateTimeLocalValue(selectedOptimization.seedForm.startDate),
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : null}
          </div>
        </>
      )}
    </PrototypeStudioLayout>
  );
}
